import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';

const FALLBACK_DOCS = [
  { slug: 'index', title: 'Zagger Overview', path: '/docs/index.html' },
  { slug: 'roles-and-access', title: 'Roles and Access', path: '/docs/roles-and-access.html' },
  { slug: 'google-auth', title: 'Google Authentication', path: '/docs/google-auth.html' },
  { slug: 'ingestion-sources', title: 'Ingestion Source Types', path: '/docs/ingestion-sources.html' },
  { slug: 'ingestion-groups', title: 'Building an Ingestion Group', path: '/docs/ingestion-groups.html' },
  { slug: 'api-ingestion', title: 'API Ingestion and Deduplication', path: '/docs/api-ingestion.html' },
  { slug: 'transform-ingestion', title: 'Transformations', path: '/docs/transform-ingestion.html' },
  { slug: 'transform-sql-ingestion', title: 'SQL Transformations', path: '/docs/transform-sql-ingestion.html' },
  { slug: 'schedules', title: 'Schedules and Manual Ingestion', path: '/docs/schedules.html' },
  { slug: 'search-fields', title: 'Search Fields and Raw Keys', path: '/docs/search-fields.html' },
  { slug: 'tools', title: 'Vector Tools and Embeddings', path: '/docs/tools.html' },
  { slug: 'analytics', title: 'Analytics Tools', path: '/docs/analytics.html' },
  { slug: 'filter', title: 'Filter Tools', path: '/docs/filter.html' },
  { slug: 'prompts', title: 'Prompts', path: '/docs/prompts.html' },
  { slug: 'prompt-templating', title: 'Prompt Templating', path: '/docs/prompt-templating.html' },
  { slug: 'yuml', title: 'YUML Output Templates', path: '/docs/yuml.html' },
  { slug: 'research', title: 'Deep Research Agent', path: '/docs/research.html' },
  { slug: 'views', title: 'Views', path: '/docs/views.html' },
  { slug: 'projects', title: 'Projects', path: '/docs/projects.html' },
  { slug: 'settings-system-vs-agent', title: 'System vs Agent Settings', path: '/docs/settings-system-vs-agent.html' },
  { slug: 'workflows', title: 'Workflows and Project Chat', path: '/docs/workflows.html' },
  { slug: 'headless', title: 'Headless API', path: '/docs/headless.html' },
  { slug: 'headless-user-choice', title: 'Headless user choice', path: '/docs/headless-user-choice.html' },
  { slug: 'headless-postgrest', title: 'Headless PostgREST', path: '/docs/headless-postgrest.html' },
  { slug: 'system-test-harness', title: 'System Test Harness', path: '/docs/system-test-harness.html' },
  { slug: 'system-logs', title: 'System Logs', path: '/docs/system-logs.html' },
];

function applyIframeTheme(iframe, isDark) {
  try {
    const doc = iframe?.contentDocument;
    if (!doc?.documentElement) return;
    doc.documentElement.classList.toggle('dark-mode', Boolean(isDark));
    doc.documentElement.classList.toggle('light-mode', !isDark);
    doc.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
    if (doc.body) {
      doc.body.style.background = '';
      doc.body.style.color = '';
    }
  } catch (_) {
    // Cross-origin iframe; ignore.
  }
}

function HelpDocsPage() {
  const { darkMode } = useAuth();
  const iframeRef = useRef(null);
  const [docs, setDocs] = useState(FALLBACK_DOCS);
  const [activePath, setActivePath] = useState(FALLBACK_DOCS[0].path);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/help/info?ensure=0`, { headers });
        const json = await resp.json().catch(() => ({}));
        if (!cancelled && resp.ok && json?.success && Array.isArray(json.data?.docs) && json.data.docs.length > 0) {
          setDocs(json.data.docs);
          setActivePath(json.data.docs[0].path || FALLBACK_DOCS[0].path);
        }
      } catch (_) {
        // use fallback list
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const iframeSrc = useMemo(() => {
    const base = process.env.PUBLIC_URL || '';
    return `${base}${activePath}`;
  }, [activePath]);

  const syncIframeTheme = useCallback(() => {
    applyIframeTheme(iframeRef.current, darkMode);
  }, [darkMode]);

  useEffect(() => {
    syncIframeTheme();
  }, [syncIframeTheme, iframeSrc]);

  return (
    <div className="help-docs-page">
      <aside className="help-docs-sidebar">
        <h2>Documentation</h2>
        {docs.map((doc) => (
          <a
            key={doc.slug || doc.path}
            href={doc.path}
            className={`help-docs-link${activePath === doc.path ? ' active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              setActivePath(doc.path);
            }}
          >
            {doc.title}
          </a>
        ))}
      </aside>
      <div className="help-docs-frame-wrap">
        <iframe
          ref={iframeRef}
          className="help-docs-frame"
          title="Zagger product documentation"
          src={iframeSrc}
          onLoad={syncIframeTheme}
        />
      </div>
    </div>
  );
}

export default HelpDocsPage;
